import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import NavBar from "@/components/NavBar";
import { api } from "@/lib/api";

export default function GalleryPage() {
  const [params, setParams] = useSearchParams();
  const classId = params.get("class") || "";
  const [lightbox, setLightbox] = useState(null); // photo object or null

  const { data: photos = [], isLoading } = useQuery({
    queryKey: ["publicGallery", classId],
    queryFn: async () => {
      const q = classId ? `?class_id=${classId}` : "";
      return (await api.get(`/gallery/photos${q}`)).data;
    },
  });

  // Fetch class list for the filter chips
  const { data: classes = [] } = useQuery({
    queryKey: ["publicClassesForGallery"],
    queryFn: async () => (await api.get("/classes?include_past=true")).data,
  });

  const classOptions = (classes || []).filter((c) =>
    photos.some((p) => p.class_id === c.id) || c.id === classId
  );

  // Set document title + meta for the gallery page (SEO)
  if (typeof document !== "undefined") {
    document.title = "Photo Gallery — Graceful Garden Coaching";
  }

  return (
    <>
      <NavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <header className="mb-8 sm:mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">Moments from the homestead</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] mt-2">Photo Gallery</h1>
          <p className="text-gg-muted mt-3 max-w-2xl">
            Snapshots from past classes — kids harvesting, kneading, hatching, planting. See what your child could be doing next.
          </p>
        </header>

        {classOptions.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-class-filters">
            <FilterChip
              active={!classId}
              onClick={() => setParams({})}
              label="All photos"
              testId="gallery-filter-all"
            />
            {classOptions.map((c) => (
              <FilterChip
                key={c.id}
                active={classId === c.id}
                onClick={() => setParams({ class: c.id })}
                label={c.title}
                testId={`gallery-filter-${c.id}`}
              />
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-square rounded-2xl bg-gg-surface border border-gg-border animate-pulse" />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className="rounded-2xl border border-gg-border bg-gg-surface p-16 text-center">
            <p className="text-gg-muted">Heather hasn't shared any photos yet — check back after the next class!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="gallery-grid">
            {photos.map((p) => (
              <GalleryCard key={p.id} photo={p} onOpen={() => setLightbox(p)} />
            ))}
          </div>
        )}
      </main>

      {lightbox && <Lightbox photo={lightbox} onClose={() => setLightbox(null)} />}
    </>
  );
}

function FilterChip({ active, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
        active
          ? "bg-gg-green text-white border-gg-green"
          : "bg-gg-surface border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green"
      }`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function GalleryCard({ photo, onOpen }) {
  return (
    <figure
      className="group relative aspect-square rounded-2xl overflow-hidden border border-gg-border bg-gg-surface cursor-pointer"
      onClick={onOpen}
      data-testid={`gallery-photo-${photo.id}`}
    >
      <img
        src={photo.image_url}
        alt={photo.alt_text || photo.caption || "Graceful Garden class photo"}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {(photo.caption || photo.class_title) && (
        <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white p-3 text-xs translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
          {photo.class_title && (
            <p className="font-semibold uppercase tracking-[0.14em] text-[10px] mb-0.5 opacity-90">
              {photo.class_title}
            </p>
          )}
          {photo.caption && <p className="line-clamp-2">{photo.caption}</p>}
        </figcaption>
      )}
    </figure>
  );
}

function Lightbox({ photo, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
        data-testid="gallery-lightbox-close"
      >
        <X size={24} />
      </button>
      <figure
        className="max-w-5xl max-h-full w-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.image_url}
          alt={photo.alt_text || photo.caption || "Graceful Garden class photo"}
          className="max-h-[80vh] w-auto rounded-xl object-contain shadow-2xl"
        />
        {(photo.caption || photo.class_title) && (
          <figcaption className="text-white/90 text-center mt-4 max-w-2xl">
            {photo.class_title && (
              <p className="text-xs uppercase tracking-[0.18em] text-gg-green/90 font-semibold mb-1">
                {photo.class_title}
              </p>
            )}
            {photo.caption && <p>{photo.caption}</p>}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
